import { message as antdMessage, notification as antdNotification } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import type { NotificationInstance } from 'antd/es/notification/interface';

/**
 * Centralized notification service. Calls fall back to antd's static APIs
 * when the provider hasn't initialized — this keeps notifications working
 * for federated remotes that mount before host bootstrap completes, and
 * for non-React callers (axios interceptors).
 */

interface NotificationConfig {
  message: string;
  description?: string;
  duration?: number;
}

// Singleton instances set by NotificationProvider
let messageApi: MessageInstance | null = null;
let notificationApi: NotificationInstance | null = null;

const getMessageApi = (): MessageInstance => messageApi ?? (antdMessage as unknown as MessageInstance);
const getNotificationApi = (): NotificationInstance =>
  notificationApi ?? (antdNotification as unknown as NotificationInstance);

/**
 * Initialize notification APIs (called from NotificationProvider)
 */
export function initializeNotifications(
  message: MessageInstance,
  notification: NotificationInstance
) {
  messageApi = message;
  notificationApi = notification;
}

/**
 * Show a toast message (small, auto-dismissing)
 */
export const toast = {
  success: (content: string, duration = 3) => {
    getMessageApi().success(content, duration);
  },
  error: (content: string, duration = 4) => {
    getMessageApi().error(content, duration);
  },
  warning: (content: string, duration = 3) => {
    getMessageApi().warning(content, duration);
  },
  info: (content: string, duration = 3) => {
    getMessageApi().info(content, duration);
  },
  loading: (content: string, duration = 0) => {
    return getMessageApi().loading(content, duration);
  },
};

/**
 * Show a notification (larger, with title and description)
 */
export const notify = {
  success: (config: NotificationConfig) => {
    getNotificationApi().success({
      message: config.message,
      description: config.description,
      duration: config.duration ?? 4.5,
      placement: 'topRight',
    });
  },
  error: (config: NotificationConfig) => {
    getNotificationApi().error({
      message: config.message,
      description: config.description,
      duration: config.duration ?? 6,
      placement: 'topRight',
    });
  },
  warning: (config: NotificationConfig) => {
    getNotificationApi().warning({
      message: config.message,
      description: config.description,
      duration: config.duration ?? 4.5,
      placement: 'topRight',
    });
  },
  info: (config: NotificationConfig) => {
    getNotificationApi().info({
      message: config.message,
      description: config.description,
      duration: config.duration ?? 4.5,
      placement: 'topRight',
    });
  },
};

/**
 * Show API error notification with consistent formatting.
 * Skips status codes already handled by the Axios interceptor (401, 403, 5xx)
 * to prevent duplicate toasts.
 */
export function showApiError(error: unknown, fallbackMessage = 'An error occurred') {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: number }).status;
    if (status === 401 || status === 403 || (status && status >= 500)) {
      return;
    }
  }
  const message = getFriendlyErrorMessage(error, fallbackMessage);
  toast.error(message);
}

/**
 * Handle mutation errors. Only shows a toast for status codes
 * NOT already handled by the Axios interceptor (401, 403, 5xx).
 */
export function handleMutationError(error: unknown, fallbackMessage = 'An error occurred') {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: number }).status;
    if (status === 401 || status === 403 || (status && status >= 500)) {
      return;
    }
  }
  const message = getFriendlyErrorMessage(error, fallbackMessage);
  toast.error(message);
}

/**
 * Known backend error patterns mapped to user-friendly messages.
 * Partial-match: if the lowercased raw message contains the key, the friendly
 * message is returned. Add new entries as domain errors are encountered.
 */
const ERROR_MESSAGE_MAP: [pattern: string, friendly: string][] = [
  ['license pool exhausted', 'No more licenses are available in this pool.'],
  ['device already activated', 'This device is already activated under another allocation.'],
  ['cannot revoke', 'This device cannot be revoked while it has active sessions.'],
  ['duplicate key', 'A record with this name already exists.'],
  ['already exists', 'A record with these details already exists.'],
  ['not found', 'The requested resource was not found.'],
  ['feature not available', 'This feature is not available in the current deployment.'],
  ['quota exceeded', 'The allocation limit has been reached.'],
  ['invalid email or password', 'The email or password you entered is incorrect.'],
  ['authentication failed', 'The email or password you entered is incorrect.'],
];

/**
 * Map raw backend error messages to user-friendly alternatives.
 * Returns the fallback when no known pattern matches.
 */
export function friendlyErrorMessage(raw: string, fallback: string): string {
  const lower = raw.toLowerCase();
  for (const [pattern, friendly] of ERROR_MESSAGE_MAP) {
    if (lower.includes(pattern)) return friendly;
  }
  return fallback;
}

/**
 * Extract error message from various error formats
 */
export function getErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  if (!error) return fallback;

  if (typeof error === 'string') return error;

  if (error instanceof Error) return error.message;

  if (typeof error === 'object') {
    const err = error as Record<string, unknown>;
    if (err.data && typeof err.data === 'object') {
      const data = err.data as Record<string, unknown>;
      if (typeof data.message === 'string') return data.message;
    }
    if (typeof err.message === 'string') return err.message;
    if (typeof err.error === 'string') return err.error;
  }

  return fallback;
}

/**
 * Extract error message and run it through the friendly-message mapping.
 */
export function getFriendlyErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  const raw = getErrorMessage(error, fallback);
  return friendlyErrorMessage(raw, raw);
}
